import type {
  Customer,
  OrderItem,
  OrderTokens,
} from '../../graphql/query/getOrder';
import type {
  DeliveryOption,
  DeliveryVariables,
  Location,
} from '../../graphql/query/getDelivery';
import type { RefundOptionsVariables } from '../../graphql/query/getRefundOptions';
import type { RefundContentData } from './RefundTask/RefundTaskSchema';
import type {
  TaskConfig,
  TaskMode,
  TaskScope,
} from '../../workflow/schemaTypes';
import { type ExtractedReason, buildReasonFields } from '../../utils/item';
import type { ResolutionByItem } from '../../hooks/useResolutions';
import type { ProductVariant } from '../../graphql/query/getResolutionOptions';
import type { WorkflowData } from '../../workflow';
import type { MethodData } from './MethodTask/MethodTaskSchema';

type Variables = DeliveryVariables | RefundOptionsVariables;

type ExchangeItem = {
  id: string;
  productId: string;
  variantId: string;
  sku: string;
  variantInfo: { name: string; value: string }[];
  price: {
    currency: string;
    formattedString: string;
    value: number;
  };
};

type ExchangeInfo = {
  exchange: ExchangeItem;
  exchangeVariant?: ProductVariant;
  sameProduct: boolean;
  otherProducts: boolean;
};

export const getExchange = (
  resolutionData?: ResolutionByItem,
): ExchangeInfo | null => {
  const selectedResolution = resolutionData?.selected;
  const resolutionOptions = resolutionData?.options;

  if (!selectedResolution?.exchangeItem?.variantId) {
    return null;
  }

  const exchangeItemIds = selectedResolution?.exchangeItem || {};

  const allVariants: ProductVariant[] = (resolutionOptions ?? []).flatMap(
    (group) => [
      ...(group?.exchanges?.sameProduct?.variants || []),
      ...(group?.exchanges?.otherProducts?.flatMap((p) => p.variants) || []),
    ],
  );

  const exchangeVariant: ProductVariant | undefined = allVariants?.find(
    (variant) =>
      variant.productId === exchangeItemIds?.productId &&
      variant.variantId === exchangeItemIds?.variantId,
  );

  const sameProductVariants: ProductVariant[] =
    resolutionOptions?.flatMap(
      (group) => group?.exchanges?.sameProduct?.variants || [],
    ) || [];

  const otherProductVariants: ProductVariant[] =
    resolutionOptions?.flatMap((group) =>
      (group?.exchanges?.otherProducts || []).flatMap((p) => p.variants),
    ) || [];

  if (exchangeVariant) {
    const isSameProduct = sameProductVariants.some(
      (v) =>
        v.productId === exchangeVariant.productId &&
        v.variantId === exchangeVariant.variantId,
    );
    const isOtherProducts = otherProductVariants.some(
      (v) =>
        v.productId === exchangeVariant.productId &&
        v.variantId === exchangeVariant.variantId,
    );

    const exchangeItem = {
      id: exchangeVariant.productId ?? '',
      productId: exchangeVariant.productId ?? '',
      variantId: exchangeVariant.variantId ?? '',
      sku: exchangeVariant.sku ?? '',
      variantInfo: exchangeVariant.attributes ?? [],
      price: {
        currency: exchangeVariant?.price?.currency ?? '',
        formattedString: exchangeVariant?.price?.formattedString ?? '',
        value: exchangeVariant?.price?.value ?? 0,
      },
    };

    return {
      exchange: exchangeItem,
      exchangeVariant,
      sameProduct: isSameProduct,
      otherProducts: isOtherProducts,
    };
  }

  return null;
};

export const getInput = ({
  requestId,
  customer,
  items,
  formItems,
  orderTokens,
  orderId,
  reasons,
  deliveryMethod,
  refund,
  location,
  resolutionsByItem,
}: {
  requestId?: string | null;
  customer?: Customer;
  items?: OrderItem[];
  formItems: WorkflowData['items'];
  orderTokens?: OrderTokens;
  orderId?: string;
  reasons?: Record<OrderItem['id'], ExtractedReason>;
  deliveryMethod?: DeliveryOption &
    Pick<NonNullable<MethodData['returnMethod']>, 'address'>;
  refund?: RefundContentData;
  location?: Location;
  resolutionsByItem?: Record<OrderItem['id'], ResolutionByItem>;
}): Variables['input'] => {
  const vars: Variables['input'] & { orderMetaData?: { omsOrderId: string } } =
    {
      cart: {
        id: requestId,
        customerId: customer?.id,
        ...(deliveryMethod && {
          delivery: {
            type: deliveryMethod.type,
            serviceProviderId: deliveryMethod.serviceProvider.moniker ?? '',
            packagingOption: deliveryMethod.packagingOption,
            service: deliveryMethod.serviceProvider.name,
            fromAddress: deliveryMethod.address,
          },
        }),
        location,
        items: Object.keys(formItems || {})
          .map((itemId) => {
            // Get the actual item ID from the stored itemSelection data
            const formItemData = formItems?.[itemId];
            const actualItemId = formItemData?.itemSelection?.id;

            if (!actualItemId) {
              return null;
            }

            // Try form key first if items are from useItems(), fall back to actualItemId for items from order.items
            const orderItem =
              items?.find((item) => item.id === itemId) ??
              items?.find((item) => item.id === actualItemId);
            if (!orderItem) {
              return null;
            }

            const reasonForItem = reasons?.[itemId];
            const reasonFields = reasonForItem
              ? buildReasonFields(reasonForItem)
              : [];

            const exchangeInfo = getExchange(
              resolutionsByItem?.[itemId],
            )?.exchange;

            const result = {
              id: actualItemId,
              quantity: formItems?.[itemId]?.itemSelection?.quantity ?? 1,
              resolution:
                resolutionsByItem?.[itemId]?.selected.resolutionId ?? '',
              item: {
                id: actualItemId,
                sku: orderItem.sku ?? '',
                variantId: orderItem.variant?.id ?? '',
              },
              reason: {
                id: reasonForItem?.selectedReason?.code ?? '',
                title: reasonForItem?.selectedReason?.title ?? '',
                fields: reasonFields,
              },
              shipmentTrackingNumber:
                formItems?.[itemId]?.itemSelection?.trackingNumber ?? undefined,
              ...(exchangeInfo ? { exchange: exchangeInfo } : {}),
            };

            return result;
          })
          .filter((item): item is NonNullable<typeof item> => item !== null),
        ...(refund && {
          refund: refund?.method ? { type: refund.method } : undefined,
        }),
      },
      orderTokens,
      ...(orderId && {
        orderMetaData: {
          omsOrderId: orderId,
        },
      }),
    };

  return vars;
};

// Depending on the scenario, we may need to group tasks in the UI.  We do this using the task scope.
// Note: It is important to not reorder the tasks, as that is handled by the workflow config.
export type GroupedItems = {
  id: string;
  scope: TaskScope;
  tasks: TaskConfig[];
  mode: TaskMode;
};

export const groupConsecutiveTasksByScope = (
  tasks: TaskConfig[],
): GroupedItems[] => {
  const result: GroupedItems[] = [];
  let currentGroup: TaskConfig[] = [];
  let currentScope: TaskScope | null = null;
  let currentMode: TaskMode = 'enabled';

  const addGroup = (
    scope: TaskScope | null,
    tasks: TaskConfig[],
    mode: TaskMode,
    i: number,
  ) => {
    if (scope && tasks.length > 0) {
      result.push({ id: `group-${scope}-${i}`, scope, tasks, mode });
    }
  };

  for (let i = 0; i < tasks.length; i++) {
    const task = tasks[i];

    if (task?.scope === currentScope) {
      currentGroup.push(task);
    } else {
      addGroup(currentScope, currentGroup, currentMode, i);
      currentGroup = task ? [task] : [];
      currentScope = task?.scope ?? currentScope;
      currentMode = task?.mode ?? currentMode;
    }
  }

  addGroup(currentScope, currentGroup, currentMode, tasks.length);

  return result;
};
